import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useMasterData } from "../master-data/api";
import { toUserMap, useUsers, userName } from "../users/api";
import { StatusBadge } from "../incidents/components/status-badge";
import { useOperationsLogEntry } from "./api";
import { OperationsLogTypeBadge } from "./entry-parts";

/**
 * One Betriebslog entry (Hauptansicht 5, "Einzel-Detailsicht"; [F-015]) and
 * the incidents linked to it - [F-013]'s other end, which [AC-014] reads:
 * "der Vorfall erscheint in der Detailansicht des Betriebslog-Eintrags".
 * Entries are not edited: the matrix knows recording and linking only.
 */
export function OperationsLogEntryPage(): JSX.Element {
  const { id } = useParams({ from: "/operations-log/$id" });
  const { t } = useTranslation();
  const entryQuery = useOperationsLogEntry(id);
  const master = useMasterData();
  const usersQuery = useUsers();
  const userById = useMemo(() => toUserMap(usersQuery.data), [usersQuery.data]);

  const back = (
    <Link
      to="/operations-log"
      data-testid="operations-log-entry_back"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {t("operationsLog.detail.back")}
    </Link>
  );

  if (entryQuery.isLoading) {
    return (
      <div className="space-y-6">
        {back}
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (entryQuery.isError || !entryQuery.data) {
    return (
      <div className="space-y-6">
        {back}
        <div
          role="alert"
          data-testid="operations-log-entry_load-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
        >
          <span>{t("errors.load")}</span>
          <Button variant="outline" size="sm" data-testid="operations-log-entry_retry" onClick={() => void entryQuery.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  const entry = entryQuery.data;
  const areaNames = entry.areaIds
    .map((areaId) => master.areaById.get(areaId)?.name)
    .filter((name): name is string => Boolean(name));

  return (
    <div className="space-y-6">
      {back}

      <Card data-testid="operations-log-entry_header">
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid="operations-log-entry_type">
              <OperationsLogTypeBadge type={entry.type} />
            </span>
            <span className="text-sm text-muted-foreground">{formatDateTime(entry.startAt)}</span>
          </div>
          <CardTitle data-testid="operations-log-entry_description" className="whitespace-pre-wrap text-xl">
            {entry.shortDescription}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Fact label={t("operationsLog.detail.start")} testid="operations-log-entry_start">
              {formatDateTime(entry.startAt)}
            </Fact>
            <Fact label={t("operationsLog.detail.end")} testid="operations-log-entry_end">
              {formatDateTime(entry.endAt)}
            </Fact>
            <Fact label={t("operationsLog.detail.areas")} testid="operations-log-entry_areas">
              {areaNames.length > 0 ? areaNames.join(", ") : t("common.none")}
            </Fact>
            <Fact label={t("operationsLog.detail.recordedBy")} testid="operations-log-entry_recorded-by">
              {userName(entry.recordedById, userById)}
            </Fact>
            <Fact label={t("operationsLog.detail.recordedAt")} testid="operations-log-entry_recorded-at">
              {formatDateTime(entry.createdAt)}
            </Fact>
          </dl>
        </CardContent>
      </Card>

      <Card data-testid="operations-log-entry_incidents">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("operationsLog.detail.incidents")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entry.incidents.length === 0 ? (
            <p data-testid="operations-log-entry_incidents-empty" className="text-sm text-muted-foreground">
              {t("operationsLog.detail.noIncidents")}
            </p>
          ) : (
            <ul className="divide-y">
              {entry.incidents.map((incident) => (
                <li key={incident.id} className="flex flex-wrap items-center gap-3 py-2">
                  <Link
                    to="/incidents/$id"
                    params={{ id: incident.id }}
                    data-testid={`operations-log-entry_incident_${incident.number}`}
                    className="font-mono text-sm text-primary-text underline-offset-4 hover:underline"
                  >
                    {incident.number}
                  </Link>
                  <span className="text-sm font-medium">{incident.title}</span>
                  <StatusBadge status={incident.status} />
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">{t("operationsLog.detail.linkHint")}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Fact({ label, testid, children }: { label: string; testid: string; children: ReactNode }): JSX.Element {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd data-testid={testid} className="text-sm">
        {children}
      </dd>
    </div>
  );
}
